import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { alpha, Collapse, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'

interface NestedNavItemProps {
    nestingLevel: number
    isVisible: boolean
    openParent: () => void
}

const NestedNavItemContext = React.createContext<NestedNavItemProps>({
    nestingLevel: 0,
    isVisible: true,
    openParent: () => {},
})

const StyledListItem = styled(ListItem)<{ $nestingLevel: number; $isSelected: boolean }>`
    cursor: pointer;
    padding-left: ${({ theme, $nestingLevel }) => theme.spacing(2 + $nestingLevel * 2)};
    background-color: ${({ theme, $isSelected }) => ($isSelected ? alpha(theme.palette.primary.main, 0.2) : '')};
    color: ${({ theme, $isSelected }) => ($isSelected ? theme.palette.primary.main : '')};
    width: 90%;
    margin: auto;
    border-radius: 5px;
`

const StyledListItemIcon = styled(ListItemIcon)<{ $isSelected: boolean }>`
    color: ${({ theme, $isSelected }) => ($isSelected ? theme.palette.primary.main : '')};
`

export interface NavItemProps {
    icon?: React.ReactNode
    className?: string
    title: string
    href?: string
    isAllowed: boolean
    hideWithNoChildren?: boolean
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => unknown
    children?: React.ReactNode
}

export function NavItem({ icon, className, title, href, hideWithNoChildren, isAllowed, onClick, children }: NavItemProps) {
    let location = useLocation()

    const [open, setOpen] = useState(false)
    const navigate = useNavigate()
    const ctx = useContext(NestedNavItemContext)

    const onClickHandler = useCallback(
        (e: any) => {
            href && navigate(href)
            children && setOpen((open) => !open)
            onClick?.(e)
        },
        [children, href, navigate, onClick],
    )
    const isSelected = location.pathname === href

    useEffect(() => {
        if (isSelected && !ctx.isVisible) {
            ctx.openParent()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const nestedCtx = useMemo(
        () => ({
            nestingLevel: ctx.nestingLevel + 1,
            isVisible: open,
            openParent: () => setOpen(true),
        }),
        [ctx.nestingLevel, open],
    )

    if (
        !isAllowed ||
        !ctx.isVisible ||
        (hideWithNoChildren && (!children || (Array.isArray(children) && !children.filter((c) => c.props.isAllowed).length)))
    ) {
        return null
    }
    return (
        <>
            <StyledListItem className={className} $isSelected={isSelected} $nestingLevel={ctx.nestingLevel} onClick={onClickHandler}>
                {!!icon && <StyledListItemIcon $isSelected={isSelected}>{icon}</StyledListItemIcon>}
                <ListItemText primary={title} color="primary" />
                {!!children && (open ? <ExpandLess /> : <ExpandMore />)}
            </StyledListItem>
            <Collapse in={open} timeout="auto">
                <NestedNavItemContext.Provider value={nestedCtx}>
                    <List disablePadding>{children}</List>
                </NestedNavItemContext.Provider>
            </Collapse>
        </>
    )
}
